import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import qs from "qs";
import useNavigate from "./usePartnerNavigate";
import { useQueryParams } from "./useQueryParams";

type Tab<T = string> = {
  value: T;
  label: string;
};

type UseTabNavigationProps<T = string> = {
  tabs: Tab<T>[];
};

export const useTabNavigation = <T = string>({ tabs }: UseTabNavigationProps<T>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { queryParams } = useQueryParams();

  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return tabs.find((tab) => tab.value === queryParams?.tab);
    }
    return tabs[0];
  }, [queryParams, tabs]);

  const clickTab = useCallback(
    (tab: Tab<T>) => {
      const newQueryParams = { ...queryParams };
      if (newQueryParams?.tab) {
        delete newQueryParams.tab;
      }

      newQueryParams.tab = tab.value;

      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [location, queryParams, navigate],
  );

  return {
    activeTab,
    clickTab,
  };
};
